import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * One activation status (R15, 2026-10-29), as `GET /onboarding/activation`
 * and `GET /admin/onboarding/{id}/activation` return it. The customer's
 * portal and the admin console render it with this one view, so both see the
 * same steps, owners, blockers and next actions. Staff also see what LipaFlow
 * does next (`staffAction`).
 */
export type ActivationResponsible = 'customer' | 'lipaflow' | 'bank'

export interface ActivationStep {
  id: string
  label: string
  state: 'done' | 'action_needed' | 'waiting'
  responsible: ActivationResponsible
  detail: string
  next_action: string | null
  action_route: string | null
  staff_action?: string | null
  since: string | null
}

export interface PortfolioItem {
  id: string
  label: string
  count: number
  done: boolean
  optional?: boolean
  next_action: string | null
  action_route: string | null
}

export interface ParticipantGroup {
  id: string
  label: string
  people: number
  ready: number
  ready_label: string
  waiting: number
  waiting_label: string
  needs_attention: { problem: string; count: number; detail: string }[]
  invitations?: { email: string; problem: string; detail: string | null; last_sent_at: string | null }[]
  next_action: string | null
  action_route: string | null
}

export interface ActivationStatus {
  tenant_id: string
  tenant_name: string
  vertical_code: string | null
  operating_model: string | null
  as_of: string
  organisation: {
    state: 'live' | 'ready' | 'in_progress'
    started_at: string | null
    live_at: string | null
    waiting_on: ActivationResponsible | null
    assigned_to: { id: string; name: string } | null
    steps: ActivationStep[]
  }
  portfolio: { items: PortfolioItem[]; ready: boolean }
  participants: ParticipantGroup[]
}

/** "3 days", "5 hours", "under an hour": how long since `since`, at `asOf`. */
export function elapsed(since: string | null, asOf: string): string | null {
  if (!since) return null
  const ms = new Date(asOf).getTime() - new Date(since).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.floor(hours / 24)
  return `${days} days`
}

interface Props {
  status: ActivationStatus
  /** Who is looking: the customer sees "you", staff see "the customer" and LipaFlow's next step. */
  audience: 'customer' | 'staff'
  /** Renders an action route as a link (the customer's router); staff get none. */
  renderAction?: (route: string, label: string) => ReactNode
}

/** Who a step is with, mid-sentence: "with you", "with LipaFlow". */
function who(responsible: ActivationResponsible, audience: Props['audience']): string {
  if (responsible === 'lipaflow') return 'LipaFlow'
  if (responsible === 'bank') return 'the bank'
  return audience === 'customer' ? 'you' : 'the customer'
}

const STATE_LABEL: Record<ActivationStatus['organisation']['state'], string> = {
  live: 'Live',
  ready: 'Ready: goes live on the next check',
  in_progress: 'Not live yet',
}

function StepRow({ step, audience, asOf, renderAction }: {
  step: ActivationStep; audience: Props['audience']; asOf: string; renderAction?: Props['renderAction']
}) {
  const time = elapsed(step.since, asOf)
  const Icon = step.state === 'done' ? CheckCircleIcon : step.state === 'action_needed' ? ExclamationCircleIcon : ClockIcon
  return (
    <li className={clsx('flex items-start gap-3 rounded-button border p-3',
      step.state === 'done' ? 'border-hairline bg-surface' : 'border-hairline bg-surface-alt')}>
      <Icon className={clsx('mt-0.5 h-5 w-5 shrink-0',
        step.state === 'done' ? 'text-success' : step.state === 'action_needed' ? 'text-warning' : 'text-ink-faint')}
        aria-label={step.state === 'done' ? 'Done' : step.state === 'action_needed' ? 'Needs action' : 'Waiting'} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-body-sm font-medium text-ink">{step.label}</p>
        <p className="text-caption text-ink-muted">{step.detail}</p>
        {step.state !== 'done' && (
          <p className="text-caption text-ink-muted">
            With <span className="font-medium text-ink">{who(step.responsible, audience)}</span>
            {time && <> · {time}</>}
          </p>
        )}
        {step.state === 'done' && time && <p className="text-caption text-ink-faint">Done {time} ago</p>}
        {step.state !== 'done' && step.next_action && (audience === 'staff' || !step.action_route || !renderAction) && (
          <p className="text-caption text-ink">
            {audience === 'staff' ? 'Customer next: ' : 'Next: '}{step.next_action}
          </p>
        )}
        {audience === 'staff' && step.state !== 'done' && step.staff_action && (
          <p className="text-caption text-accent">LipaFlow next: {step.staff_action}</p>
        )}
      </div>
      {audience === 'customer' && step.state !== 'done' && step.next_action && step.action_route && renderAction &&
        <div className="shrink-0">{renderAction(step.action_route, step.next_action)}</div>}
    </li>
  )
}

/** Going live, set up and invited: three tracks, shown apart. */
export function ActivationStatusView({ status, audience, renderAction }: Props) {
  const org = status.organisation
  const since = org.state === 'live' ? null : elapsed(org.started_at, status.as_of)
  const action = (route: string | null, label: string | null) =>
    audience === 'customer' && route && label && renderAction ? renderAction(route, label) : null
  return (
    <div className="space-y-6">
      <section aria-label="Going live" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-outfit text-h4 text-ink">Going live</h3>
          <span className={clsx('text-caption font-medium', org.state === 'live' ? 'text-success' : 'text-ink-muted')}>
            {STATE_LABEL[org.state]}{org.state === 'live' && org.live_at ? ` since ${new Date(org.live_at).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}{since ? ` · started ${since} ago` : ''}
          </span>
        </div>
        {org.state !== 'live' && org.waiting_on && (
          <p className="text-body-sm text-ink-muted">
            Now with <span className="font-medium text-ink">{who(org.waiting_on, audience)}</span>.
            {org.assigned_to && <> LipaFlow contact: {org.assigned_to.name}.</>}
          </p>
        )}
        {org.state === 'live' && org.steps.some((step) => step.state !== 'done') && (
          // Live is final: a step that reopens later (a newly connected account) needs attention,
          // it doesn't take the organisation back off live.
          <p className="text-body-sm text-ink-muted">You are live. These need attention, but don't stop you collecting:</p>
        )}
        <ol className="space-y-2">
          {(org.state === 'live' ? org.steps.filter((step) => step.state !== 'done') : org.steps).map((step) => (
            <StepRow key={step.id} step={step} audience={audience} asOf={status.as_of} renderAction={renderAction} />
          ))}
        </ol>
      </section>

      {status.portfolio.items.length > 0 && (
        <section aria-label="Set up to operate" className="space-y-3">
          <h3 className="font-outfit text-h4 text-ink">Set up to operate</h3>
          <p className="text-caption text-ink-muted">Separate from going live: you can set these up while you wait.</p>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {status.portfolio.items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 rounded-button border border-hairline p-3">
                <span className="min-w-0">
                  <span className="block text-body-sm text-ink">{item.label}{item.optional ? ' (optional)' : ''}</span>
                  <span className="block text-caption text-ink-muted tabular">
                    {item.count} {item.done ? '' : `· ${item.next_action ?? 'none yet'}`}
                  </span>
                </span>
                {!item.done && action(item.action_route, item.next_action)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {status.participants.length > 0 && (
        <section aria-label="People invited" className="space-y-3">
          <h3 className="font-outfit text-h4 text-ink">People invited</h3>
          <ul className="space-y-2">
            {status.participants.map((group) => (
              <li key={group.id} className="rounded-button border border-hairline p-3 space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-body-sm font-medium text-ink">{group.label}</p>
                  {group.next_action && action(group.action_route, group.next_action)}
                </div>
                <p className="text-caption text-ink-muted tabular">
                  {group.people} in all · {group.ready} {group.ready_label}
                  {group.waiting > 0 && <> · {group.waiting} {group.waiting_label}</>}
                </p>
                {group.needs_attention.map((a) => (
                  <p key={a.problem} className="text-caption text-warning">{a.count} · {a.detail}</p>
                ))}
                {group.invitations && group.invitations.length > 0 && (
                  <ul className="text-caption text-ink-muted">
                    {group.invitations.map((i) => (
                      <li key={i.email}>{i.email}: {i.problem === 'expired' ? 'expired' : `not delivered${i.detail ? ` (${i.detail})` : ''}`}</li>
                    ))}
                  </ul>
                )}
                {audience === 'staff' && group.next_action && <p className="text-caption text-ink">Customer next: {group.next_action}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

export default ActivationStatusView
